import { Injectable } from '@angular/core';
import { APP_CONFIG, AppConfig } from '../../config/app.config';
import { environment } from '../../../environments/environment';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogEntry {
  timestamp: Date;
  level: LogLevel;
  message: string;
  data?: unknown;
  context?: string;
  userId?: string;
  sessionId?: string;
}

interface WindowWithGtag extends globalThis.Window {
  gtag?: (command: string, action: string, params: Record<string, unknown>) => void;
}

@Injectable({
  providedIn: 'root'
})
export class LoggerService {
  private readonly config: AppConfig = APP_CONFIG;
  private readonly isProduction = environment.production;

  constructor() {
    this.loadLoggingConfig();
  }

  debug(message: string, data?: unknown, context?: string): void {
    this.log('debug', message, data, context);
  }

  info(message: string, data?: unknown, context?: string): void {
    this.log('info', message, data, context);
  }

  warn(message: string, data?: unknown, context?: string): void {
    this.log('warn', message, data, context);
  }

  error(message: string, error?: unknown, context?: string): void {
    this.log('error', message, error, context);
  }

  logApiCall(method: string, url: string, status: number, duration: number): void {
    const message = `API ${method} ${url} - ${status} (${duration}ms)`;
    const level = status >= 400 ? 'error' : status >= 300 ? 'warn' : 'info';

    this.log(level, message, { method, url, status, duration }, 'API');
  }

  logUserAction(action: string, details?: unknown): void {
    this.info(`User action: ${action}`, details, 'USER_ACTION');
  }

  logSecurityEvent(event: string, details?: unknown): void {
    this.warn(`Security event: ${event}`, details, 'SECURITY');
  }

  logPerformance(operation: string, duration: number): void {
    if (duration > 1000) {
      this.warn(
        `Performance issue: ${operation} took ${duration}ms`,
        { operation, duration },
        'PERFORMANCE'
      );
    } else {
      this.debug(
        `Performance: ${operation} took ${duration}ms`,
        { operation, duration },
        'PERFORMANCE'
      );
    }
  }

  logErrorWithStack(message: string, error: Error, context?: string): void {
    const errorData = {
      name: error.name,
      message: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString()
    };

    this.error(message, errorData, context);
  }

  logValidationError(field: string, value: unknown, rule: string, context?: string): void {
    this.warn(
      `Validation error: ${field} failed rule ${rule}`,
      { field, value, rule },
      context || 'VALIDATION'
    );
  }

  private log(level: LogLevel, message: string, data?: unknown, context?: string): void {
    if (!this.isLogLevelEnabled(level)) {
      return;
    }

    const logEntry: LogEntry = {
      timestamp: new Date(),
      level,
      message,
      data,
      context,
      userId: this.getCurrentUserId(),
      sessionId: this.getCurrentSessionId()
    };

    if (this.config.logging.enableConsole) {
      this.logToConsole(logEntry);
    }

    if (this.config.logging.enableRemote && this.isProduction) {
      this.logToRemote(logEntry);
    }

    this.emitLogEvent(logEntry);
  }

  private isLogLevelEnabled(level: LogLevel): boolean {
    const levels: LogLevel[] = ['debug', 'info', 'warn', 'error'];
    const configLevel = this.config.logging.level;
    const configLevelIndex = levels.indexOf(configLevel);
    const currentLevelIndex = levels.indexOf(level);

    return currentLevelIndex >= configLevelIndex;
  }

  private logToConsole(entry: LogEntry): void {
    const timestamp = entry.timestamp.toISOString();
    const prefix = `[${timestamp}] [${entry.level.toUpperCase()}]`;
    const context = entry.context ? ` [${entry.context}]` : '';

    switch (entry.level) {
      case 'debug':
        // eslint-disable-next-line no-console
        console.debug(`${prefix}${context} ${entry.message}`, entry.data || '');
        break;
      case 'info':
        // eslint-disable-next-line no-console
        console.info(`${prefix}${context} ${entry.message}`, entry.data || '');
        break;
      case 'warn':
        // eslint-disable-next-line no-console
        console.warn(`${prefix}${context} ${entry.message}`, entry.data || '');
        break;
      case 'error':
        // eslint-disable-next-line no-console
        console.error(`${prefix}${context} ${entry.message}`, entry.data || '');
        break;
    }
  }

  private logToRemote(entry: LogEntry): void {
    if (typeof window !== 'undefined' && (window as WindowWithGtag).gtag) {
      (window as WindowWithGtag).gtag!('event', 'log', {
        event_category: 'application_log',
        event_label: entry.level,
        value: entry.context || 'general'
      });
    }
  }

  private emitLogEvent(entry: LogEntry): void {
    const event = new CustomEvent('app:log', { detail: entry });
    window.dispatchEvent(event);
  }

  private getCurrentUserId(): string | undefined {
    try {
      const user = localStorage.getItem('currentUser');
      return user ? JSON.parse(user).id : undefined;
    } catch {
      return undefined;
    }
  }

  private getCurrentSessionId(): string | undefined {
    try {
      return sessionStorage.getItem('sessionId') || undefined;
    } catch {
      return undefined;
    }
  }

  setLogLevel(level: LogLevel): void {
    this.config.logging.level = level;
    this.info(`Log level changed to: ${level}`, null, 'LOGGER');
  }

  enableConsoleLogging(enable: boolean): void {
    this.config.logging.enableConsole = enable;
    this.info(`Console logging ${enable ? 'enabled' : 'disabled'}`, null, 'LOGGER');
  }

  enableRemoteLogging(enable: boolean): void {
    this.config.logging.enableRemote = enable;
    this.info(`Remote logging ${enable ? 'enabled' : 'disabled'}`, null, 'LOGGER');
  }

  private loadLoggingConfig(): void {
    try {
      const stored = localStorage.getItem('wapi_logging_config');
      if (stored) {
        const parsed = JSON.parse(stored);
        this.config.logging = { ...this.config.logging, ...parsed };
      }
    } catch {
      // Failed to load logging config, using defaults
    }
  }
}
